import type { ApprovalRequest, HubToWeb, MachineInfo, WebToHub } from '@niuma/protocol';
import { base64ToBytes } from './base64.js';

type CommandPayload = Extract<WebToHub, { type: 'web.command' }>['command'];

interface Pending {
  resolve: (data: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export interface HubHandlers {
  onMachines: (machines: MachineInfo[]) => void;
  onOutput: (machineId: string, sessionId: string, bytes: Uint8Array) => void;
  onExit: (machineId: string, sessionId: string, exitCode: number | null) => void;
  onApproval: (request: ApprovalRequest) => void;
  onStatus: (status: ConnectionStatus) => void;
}

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

const COMMAND_TIMEOUT_MS = 30000;

/**
 * Persistent, auto-reconnecting console connection to the hub. Correlates
 * request/response by reqId and decodes terminal frames to bytes for xterm.
 */
export class HubConnection {
  private ws: WebSocket | null = null;
  private readonly pending = new Map<string, Pending>();
  private seq = 0;
  private closed = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lifecycleListenersAttached = false;
  private wasBackgrounded = false;

  constructor(
    private readonly token: string,
    private readonly handlers: HubHandlers,
  ) {}

  connect(): void {
    this.closed = false;
    this.attachLifecycleListeners();
    this.open();
  }

  close(): void {
    this.closed = true;
    this.clearReconnectTimer();
    this.detachLifecycleListeners();
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    this.failAll('Connection closed');
  }

  command(machineId: string, command: CommandPayload): Promise<unknown> {
    return this.request({ type: 'web.command', reqId: this.nextId(), machineId, command });
  }

  listMachines(): Promise<unknown> {
    return this.request({ type: 'web.machine.list', reqId: this.nextId() });
  }

  decideApproval(id: string, approved: boolean): void {
    this.sendRaw({ type: 'web.approval.decision', id, approved });
  }

  private open(): void {
    if (this.closed) return;
    if (
      this.ws?.readyState === WebSocket.CONNECTING ||
      this.ws?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    this.clearReconnectTimer();
    this.handlers.onStatus('connecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(
      `${proto}://${location.host}/console?token=${encodeURIComponent(this.token)}`,
    );
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.reconnectAttempts = 0;
      this.handlers.onStatus('open');
      void this.listMachines().catch(() => undefined);
    };
    ws.onmessage = (event) => {
      if (this.ws === ws) this.handleMessage(String(event.data));
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.handlers.onStatus('closed');
      this.failAll('Connection closed');
      if (!this.closed) this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || this.closed) return;
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 15000);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private reconnectNow(): void {
    if (this.closed) return;
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    const ws = this.ws;
    this.ws = null;
    if (ws && ws.readyState !== WebSocket.CLOSED) ws.close();
    this.failAll('Connection interrupted');
    this.open();
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer === null) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private attachLifecycleListeners(): void {
    if (this.lifecycleListenersAttached) return;
    this.lifecycleListenersAttached = true;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('online', this.handleOnline);
  }

  private detachLifecycleListeners(): void {
    if (!this.lifecycleListenersAttached) return;
    this.lifecycleListenersAttached = false;
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('online', this.handleOnline);
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.wasBackgrounded = true;
      return;
    }
    if (!this.wasBackgrounded) return;
    this.wasBackgrounded = false;
    this.reconnectNow();
  };

  private readonly handlePageHide = (): void => {
    this.wasBackgrounded = true;
  };

  private readonly handlePageShow = (): void => {
    if (!this.wasBackgrounded) return;
    this.wasBackgrounded = false;
    this.reconnectNow();
  };

  private readonly handleOnline = (): void => {
    this.reconnectNow();
  };

  private request(message: WebToHub & { reqId: string }): Promise<unknown> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('Connection unavailable'));
    }
    return new Promise<unknown>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(message.reqId);
        reject(new Error('Request timed out'));
      }, COMMAND_TIMEOUT_MS);
      this.pending.set(message.reqId, { resolve, reject, timer });
      this.sendRaw(message);
    });
  }

  private handleMessage(raw: string): void {
    let message: HubToWeb;
    try {
      message = JSON.parse(raw) as HubToWeb;
    } catch {
      return;
    }
    switch (message.type) {
      case 'hub.result': {
        const pending = this.pending.get(message.reqId);
        if (pending) {
          clearTimeout(pending.timer);
          this.pending.delete(message.reqId);
          pending.resolve(message.data);
        }
        break;
      }
      case 'hub.error': {
        if (message.reqId) {
          const pending = this.pending.get(message.reqId);
          if (pending) {
            clearTimeout(pending.timer);
            this.pending.delete(message.reqId);
            pending.reject(new Error(message.message));
          }
        }
        break;
      }
      case 'hub.machines':
        this.handlers.onMachines(message.machines);
        break;
      case 'hub.session.output':
        this.handlers.onOutput(
          message.machineId,
          message.sessionId,
          base64ToBytes(message.dataB64),
        );
        break;
      case 'hub.session.exit':
        this.handlers.onExit(message.machineId, message.sessionId, message.exitCode);
        break;
      case 'hub.approval':
        this.handlers.onApproval(message.request);
        break;
    }
  }

  private failAll(reason: string): void {
    for (const [, pending] of this.pending) {
      clearTimeout(pending.timer);
      pending.reject(new Error(reason));
    }
    this.pending.clear();
  }

  private sendRaw(message: WebToHub): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private nextId(): string {
    this.seq += 1;
    return `w_${Date.now().toString(36)}_${this.seq}`;
  }
}
